import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Award,
  BookOpen,
  CheckCircle2,
  Clock,
  GraduationCap,
  PlayCircle,
  Target,
  TrendingUp,
} from 'lucide-react';
import { useAuth } from '@/features/auth/AuthContext';
import { useLanguage } from '@/hooks/useLanguage';
import { api } from '@/lib/api';
import type { LearnerOverview } from '@/types/api';
import { formatDate, formatDuration, formatPercent } from '@/lib/utils';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  Progress,
  ProgressRing,
} from '@/components/ui';

export function DashboardPage() {
  const { user } = useAuth();
  const { t, language, pick } = useLanguage();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['progress', 'overview'],
    queryFn: () => api.get<LearnerOverview>('/progress/overview'),
  });

  if (isLoading) return <LoadingState />;
  if (isError || !data) return <ErrorState onRetry={() => void refetch()} />;

  const { enrollment, stats, modules, nextSession, recentAttempts, certificate } = data;

  if (!enrollment) {
    return (
      <>
        <PageHeader title={t('dashboard.greeting', { name: user?.firstName ?? '' })} />
        <Card>
          <EmptyState
            icon={<GraduationCap className="h-6 w-6" />}
            title={t('dashboard.notEnrolled')}
            description={t('dashboard.notEnrolledBody')}
          />
        </Card>
      </>
    );
  }

  const progress = enrollment.progressPercent;

  return (
    <>
      <PageHeader
        title={t('dashboard.greeting', { name: user?.firstName ?? '' })}
        description={t('dashboard.greetingSubtitle')}
      />

      {/* Bandeau de progression */}
      <Card className="mb-6 overflow-hidden">
        <div className="flex flex-col gap-6 p-6 sm:flex-row sm:items-center">
          <ProgressRing value={progress} size={132}>
            <span className="font-display text-2xl font-bold text-ink">{Math.round(progress)}%</span>
            <span className="text-[11px] uppercase tracking-wide text-ink-subtle">
              {t('dashboard.overallProgress')}
            </span>
          </ProgressRing>

          <div className="min-w-0 flex-1 space-y-4">
            <div>
              <h2 className="font-display text-lg font-semibold text-ink">{pick(enrollment.program, 'title')}</h2>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-ink-muted">
                {enrollment.cohort ? (
                  <Badge tone="primary">
                    {t('dashboard.cohort')} · {pick(enrollment.cohort, 'name')}
                  </Badge>
                ) : null}
                {enrollment.dueDate ? (
                  <Badge tone="warning">
                    {t('dashboard.dueDate')} · {formatDate(enrollment.dueDate, language)}
                  </Badge>
                ) : null}
                <span>
                  {stats.completedSessions} / {stats.totalSessions} {t('learn.sessions').toLowerCase()}
                </span>
              </div>
            </div>

            <Progress value={progress} />

            {nextSession ? (
              <div className="flex flex-col gap-3 rounded-xl bg-surface-muted p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">
                    {t('dashboard.nextSession')}
                  </p>
                  <p className="truncate font-medium text-ink">{pick(nextSession, 'title')}</p>
                  <p className="truncate text-xs text-ink-muted">{pick(nextSession, 'moduleTitle')}</p>
                </div>
                <Link to={`/learn/sessions/${nextSession.id}`} className="shrink-0">
                  <Button icon={<PlayCircle className="h-4 w-4" />}>
                    {progress > 0 ? t('dashboard.resume') : t('dashboard.startTraining')}
                  </Button>
                </Link>
              </div>
            ) : null}
          </div>
        </div>
      </Card>

      {certificate ? (
        <Card className="mb-6 border-success/30 bg-success-soft">
          <CardBody className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <Award className="mt-0.5 h-6 w-6 shrink-0 text-success" aria-hidden />
              <div>
                <p className="font-display font-semibold text-ink">{t('dashboard.certificateReady')}</p>
                <p className="text-sm text-ink-muted">{t('dashboard.certificateReadyBody')}</p>
              </div>
            </div>
            <Link to="/certificates" className="shrink-0">
              <Button variant="outline">{t('dashboard.viewCertificate')}</Button>
            </Link>
          </CardBody>
        </Card>
      ) : null}

      {/* Indicateurs personnels */}
      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          icon={<CheckCircle2 className="h-5 w-5" />}
          label={t('dashboard.sessionsCompleted')}
          value={`${stats.completedSessions} / ${stats.totalSessions}`}
          tone="success"
        />
        <StatTile
          icon={<Clock className="h-5 w-5" />}
          label={t('dashboard.timeSpent')}
          value={formatDuration(stats.timeSpentSeconds, language)}
          tone="info"
        />
        <StatTile
          icon={<Target className="h-5 w-5" />}
          label={t('dashboard.averageScore')}
          value={stats.averageScore === null ? '—' : formatPercent(stats.averageScore, language)}
          tone="accent"
        />
        <StatTile
          icon={<TrendingUp className="h-5 w-5" />}
          label={t('dashboard.quizzesPassed')}
          value={String(stats.passedQuizzes)}
          tone="primary"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Modules */}
        <Card className="lg:col-span-2">
          <CardHeader
            title={t('dashboard.modules')}
            action={
              <Link to="/learn">
                <Button variant="ghost" size="sm">
                  {t('common.seeAll')}
                </Button>
              </Link>
            }
          />
          <CardBody className="space-y-3">
            {modules.map((module) => (
              <Link
                key={module.id}
                to="/learn"
                className="flex items-center gap-4 rounded-xl border border-border p-4 transition-all hover:border-primary/40 hover:shadow-card"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-soft font-display text-sm font-bold text-primary">
                  {module.order}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-ink">{pick(module, 'title')}</p>
                  <div className="mt-2 flex items-center gap-3">
                    <Progress value={module.progressPercent} size="sm" className="flex-1" />
                    <span className="shrink-0 text-xs font-medium text-ink-muted">
                      {module.completedSessions}/{module.totalSessions}
                    </span>
                  </div>
                </div>
                {module.progressPercent === 100 ? (
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-success" aria-hidden />
                ) : null}
              </Link>
            ))}
          </CardBody>
        </Card>

        {/* Derniers resultats */}
        <Card>
          <CardHeader title={t('dashboard.recentResults')} />
          <CardBody>
            {recentAttempts.length === 0 ? (
              <EmptyState
                icon={<BookOpen className="h-6 w-6" />}
                title={t('dashboard.noRecentResults')}
                className="py-8"
              />
            ) : (
              <ul className="space-y-3">
                {recentAttempts.map((attempt) => {
                  const percent = attempt.maxScore > 0 ? (attempt.score / attempt.maxScore) * 100 : 0;
                  return (
                    <li key={attempt.id} className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-ink">{pick(attempt.quiz, 'title')}</p>
                        <p className="text-xs text-ink-subtle">{formatDate(attempt.submittedAt, language)}</p>
                      </div>
                      <Badge tone={attempt.passed ? 'success' : 'danger'}>{formatPercent(percent, language, 0)}</Badge>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  );
}

function StatTile({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone: 'primary' | 'success' | 'info' | 'accent';
}) {
  const tones = {
    primary: 'bg-primary-soft text-primary',
    success: 'bg-success-soft text-success',
    info: 'bg-info-soft text-info',
    accent: 'bg-accent-soft text-accent',
  } as const;

  return (
    <Card>
      <CardBody className="flex items-center gap-4">
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tones[tone]}`}>{icon}</span>
        <div className="min-w-0">
          <p className="truncate text-xs font-medium uppercase tracking-wide text-ink-subtle">{label}</p>
          <p className="font-display text-xl font-bold text-ink">{value}</p>
        </div>
      </CardBody>
    </Card>
  );
}
